import { useState } from "react";
import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselPrevious,
  CarouselNext
} from "@/components/ui/carousel";

type Partner = {
  name: string;
  description: string;
  logo: string;
  website: string;
};

// Add new partners here — the layout adjusts automatically (1, 2, or 3 up
// stay as a static grid; 4+ becomes a scrollable carousel).
export const partners: Partner[] = [
  {
    name: "AA8 Corporation",
    description: "AA8 bridges the gap for underserved communities, forging an equitable workforce through hands-on robotics, AI, and cybersecurity education in rural North Carolina.",
    logo: "/partner-aa8-logo.png",
    website: "https://aa8corp.org"
  },
  {
    name: "HUBZone Technology Initiative",
    description:
      "HTI refurbishes donated laptops into Chromebooks for underserved communities, breaking down barriers to educational and employment opportunities and helping close the digital divide.",
    logo: "/partner-hti-logo.webp",
    website: "https://hubzonetech.org"
  }
];

const PartnerCard = ({ partner }: { partner: Partner }) => {
  const [logoSrc, setLogoSrc] = useState(partner.logo);

  return (
    <div className="h-full flex flex-col items-center gap-6 bg-white/90 border border-border rounded-2xl shadow-card backdrop-blur-sm p-8 text-center">
      <div className="w-full max-w-[220px] h-[140px] flex items-center justify-center rounded-xl border border-border/80 bg-gradient-to-b from-white to-muted/20 px-6 py-6 shadow-inner">
        <img
          src={logoSrc}
          alt={`${partner.name} logo`}
          className="max-w-full max-h-full object-contain"
          loading="lazy"
          onError={() => setLogoSrc("/placeholder.svg")}
        />
      </div>

      <div className="space-y-3 flex-1 flex flex-col">
        <p className="text-base font-medium text-foreground">{partner.name}</p>
        <p className="text-muted-foreground leading-relaxed flex-1">{partner.description}</p>
        <a
          href={partner.website}
          target="_blank"
          rel="noreferrer"
          className="inline-flex items-center justify-center gap-2 text-primary font-semibold hover:gap-3 transition-all"
        >
          Visit {partner.name}
          <ArrowUpRight className="w-4 h-4" />
        </a>
      </div>
    </div>
  );
};

export const PartnersSection = () => {
  const container = {
    hidden: { opacity: 0 },
    show: {
      opacity: 1,
      transition: { staggerChildren: 0.12 }
    }
  };

  const item = {
    hidden: { opacity: 0, y: 20 },
    show: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.6 }
    }
  };

  const isCarousel = partners.length > 3;
  const gridColsClass =
    partners.length === 1 ? "grid-cols-1 max-w-md" : partners.length === 2 ? "sm:grid-cols-2" : "sm:grid-cols-2 lg:grid-cols-3";

  return (
    <section className="relative py-20 lg:py-28 overflow-hidden isolate">
      <div
        aria-hidden
        className="absolute inset-0 opacity-80"
        style={{
          backgroundImage: "var(--gradient-site)",
          backgroundAttachment: "fixed",
          backgroundSize: "100% 200%"
        }}
      />
      <div className="absolute inset-0 pointer-events-none bg-[radial-gradient(circle_at_20%_20%,hsl(48_100%_61%/.14),transparent_45%),radial-gradient(circle_at_80%_30%,hsl(211_52%_44%/.14),transparent_50%)]" />
      <div className="absolute inset-x-0 bottom-0 h-16 bg-gradient-to-t from-background/70 to-transparent" aria-hidden />

      <div className="container mx-auto px-4 relative z-10">
        <motion.div
          variants={container}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, amount: 0.3 }}
          className="max-w-5xl mx-auto space-y-12 text-center"
        >
          <motion.div variants={item} className="space-y-4">
            <p className="text-sm font-semibold uppercase tracking-[0.3em] text-primary/80">
              Our Partners
            </p>
            <h2 className="text-4xl sm:text-5xl font-light text-foreground">
              Organizations that <span className="text-primary">stand with us</span>
            </h2>
            <p className="text-lg text-muted-foreground max-w-3xl mx-auto">
              We're proud to work alongside partners who share our commitment to youth Python education.
            </p>
          </motion.div>
        </motion.div>

        <motion.div
          variants={item}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, amount: 0.3 }}
          className="mt-12"
        >
          {isCarousel ? (
            <Carousel opts={{ align: "start" }} className="max-w-5xl mx-auto">
              <CarouselContent>
                {partners.map((partner) => (
                  <CarouselItem key={partner.name} className="basis-full sm:basis-1/2 lg:basis-1/3">
                    <PartnerCard partner={partner} />
                  </CarouselItem>
                ))}
              </CarouselContent>
              <CarouselPrevious />
              <CarouselNext />
            </Carousel>
          ) : (
            <div className={`grid gap-8 max-w-5xl mx-auto ${gridColsClass}`}>
              {partners.map((partner) => (
                <PartnerCard key={partner.name} partner={partner} />
              ))}
            </div>
          )}
        </motion.div>
      </div>
    </section>
  );
};
